import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon, 
  UserGroupIcon,
  PencilIcon,
  TicketIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { api, invitesAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  trainerId?: string;
  trainer?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface Trainer {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  roles: string[];
}

interface Invite {
  id: string;
  token: string;
  roles: string[];
  maxUses: number;
  usedCount: number;
  expiresAt: string;
  isActive: boolean;
  note?: string;
  inviteUrl: string;
  createdAt: string;
  isExpired: boolean;
  isExhausted: boolean;
}

const UserManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState<'CLIENT' | 'TRAINER'>('CLIENT');
  const [inviteMaxUses, setInviteMaxUses] = useState(1);
  const [inviteExpiresInDays, setInviteExpiresInDays] = useState(14);
  const [inviteNote, setInviteNote] = useState('');

  const isConsultant = user?.roles?.includes('CONSULTANT');

  const fetchUsers = useCallback(async (search = searchTerm) => {
    if (!isConsultant) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (search && search.length >= 2) {
        params.append('search', search);
      }

      const response = await api.get(`/users/admin/all?${params}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [isConsultant, searchTerm]);

  const fetchTrainers = useCallback(async () => {
    if (!isConsultant) return;

    try {
      const response = await api.get('/users/admin/trainers');
      setTrainers(response.data.trainers || []);
    } catch (error) {
      console.error('Failed to fetch trainers:', error);
      toast.error('Failed to load trainers');
    }
  }, [isConsultant]);

  const fetchInvites = useCallback(async () => {
    if (!isConsultant) return;

    try {
      const response = await invitesAPI.list();
      setInvites(response.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
      toast.error('Failed to load invites');
    }
  }, [isConsultant]);

  useEffect(() => {
    fetchUsers();
    fetchTrainers();
    fetchInvites();
  }, [fetchUsers, fetchTrainers, fetchInvites]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length === 0 || searchTerm.length >= 2) {
        fetchUsers(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchUsers]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const response = await invitesAPI.create({
        roles: [inviteRole],
        maxUses: inviteMaxUses,
        expiresInDays: inviteExpiresInDays,
        note: inviteNote.trim() || undefined,
      });
      const invite = response.data.invite;
      setInvites((prev) => [invite, ...prev]);
      setInviteNote('');
      toast.success('Invite created');
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(invite.inviteUrl);
        toast.success('Invite link copied to clipboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCopyInvite = async (inviteUrl: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleRevokeInvite = async (id: string) => {
    try {
      await invitesAPI.revoke(id);
      setInvites((prev) =>
        prev.map((invite) =>
          invite.id === id ? { ...invite, isActive: false } : invite
        )
      );
      toast.success('Invite revoked');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to revoke invite');
    }
  };

  const handleEditUser = (userToEdit: User) => {
    setEditingUser({ ...userToEdit });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const response = await api.put(`/users/admin/${editingUser.id}`, {
        username: editingUser.username,
        email: editingUser.email,
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        roles: editingUser.roles,
        trainerId: editingUser.trainerId,
        isActive: editingUser.isActive
      });

      setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
      setEditingUser(null);
      toast.success('User updated successfully');
      
      if (user && editingUser.id === user.id) {
        const { updateUser } = useAuthStore.getState();
        updateUser({
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          roles: response.data.roles,
          createdAt: response.data.createdAt
        });
        toast.success('Your profile has been updated');
      }
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  const handleInputChange = (field: keyof User, value: any) => {
    if (editingUser) {
      setEditingUser({
        ...editingUser,
        [field]: value
      });
    }
  };

  const getRoleBadgeColor = (roles: string[]) => {
    if (roles.includes('CONSULTANT')) return 'bg-purple-100 text-purple-800';
    if (roles.includes('TRAINER')) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  if (!isConsultant) {
    return (
      <div className="text-center py-8">
        <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You need consultant privileges to access user management.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <h1 className="text-xl font-bold text-gray-900">User Management</h1>
        <p className="text-sm text-gray-600">
          {users.length} users
        </p>
      </div>

      <div className="px-4 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <TicketIcon className="h-5 w-5 text-blue-600" />
          <h2 className="text-sm font-semibold text-gray-900">Registration invites</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          New users must register with an invite link you create here.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'CLIENT' | 'TRAINER')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="CLIENT">Client</option>
              <option value="TRAINER">Trainer</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max uses</label>
            <input
              type="number"
              min={1}
              max={100}
              value={inviteMaxUses}
              onChange={(e) => setInviteMaxUses(Number(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expires (days)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={inviteExpiresInDays}
              onChange={(e) => setInviteExpiresInDays(Number(e.target.value) || 14)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
              placeholder="e.g. Jane Doe"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreateInvite}
          disabled={creatingInvite}
          className="w-full py-2 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 disabled:opacity-50"
        >
          {creatingInvite ? 'Creating…' : 'Create invite link'}
        </button>

        {invites.length > 0 && (
          <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
            {invites.slice(0, 10).map((invite) => {
              const inactive = !invite.isActive || invite.isExpired || invite.isExhausted;
              return (
                <div key={invite.id} className="border border-gray-200 rounded-lg p-3 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {invite.roles.join(', ')}
                        {invite.note ? ` — ${invite.note}` : ''}
                      </p>
                      <p className="text-gray-500 mt-1">
                        Uses {invite.usedCount}/{invite.maxUses} · Expires{' '}
                        {new Date(invite.expiresAt).toLocaleDateString()}
                        {inactive ? ' · inactive' : ''}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleCopyInvite(invite.inviteUrl)}
                        className="p-1 text-blue-600"
                        title="Copy link"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                      {invite.isActive && !invite.isExpired && !invite.isExhausted && (
                        <button
                          type="button"
                          onClick={() => handleRevokeInvite(invite.id)}
                          className="text-red-600 font-medium"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="sticky top-16 z-30 px-4 py-3 bg-white border-b border-gray-200 shadow-lg">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={searchTerm.length > 0 && searchTerm.length < 2 ? "Type 2+ characters to search..." : "Search users..."}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white ${
              searchTerm.length > 0 && searchTerm.length < 2 
                ? 'bg-yellow-50 text-yellow-700' 
                : 'bg-gray-50'
            }`}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-8 px-4">
          <UserGroupIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-xs text-gray-500">
            {searchTerm ? 'Try adjusting your search' : 'No users to display'}
          </p>
        </div>
      ) : (
        <div className="bg-white divide-y divide-gray-100">
          {users.map((listedUser) => (
            <div key={listedUser.id}>
              <div
                onClick={() => editingUser?.id === listedUser.id ? handleCancelEdit() : handleEditUser(listedUser)}
                className="px-4 py-3 active:bg-gray-50 touch-manipulation"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-white">
                          {listedUser.firstName?.charAt(0)}{listedUser.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {listedUser.firstName} {listedUser.lastName}
                        </h3>
                        <div className="flex items-center space-x-1 mt-1">
                          {listedUser.roles.map(role => (
                            <span key={role} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(listedUser.roles)}`}>
                              {role.toLowerCase()}
                            </span>
                          ))}
                        </div>
                        {listedUser.trainer && (
                          <p className="text-xs text-gray-500 mt-1">
                            Trainer: {listedUser.trainer.firstName} {listedUser.trainer.lastName}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <PencilIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                </div>
              </div>

              {editingUser && editingUser.id === listedUser.id && (
                <div className="px-4 py-4 bg-gray-50 border-t">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={editingUser.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={editingUser.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Username</label>
                      <input
                        type="text"
                        value={editingUser.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Roles</label>
                      <div className="space-y-2">
                        {['CONSULTANT', 'TRAINER', 'CLIENT'].map(role => (
                          <label key={role} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingUser.roles.includes(role)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleInputChange('roles', [...editingUser.roles, role]);
                                } else {
                                  handleInputChange('roles', editingUser.roles.filter(r => r !== role));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">{role.toLowerCase()}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Assigned Trainer</label>
                      <p className="text-xs text-gray-500 mb-1">Current trainerId: {editingUser.trainerId || 'none'}</p>
                      <select
                        value={editingUser.trainerId || ''}
                        onChange={(e) => handleInputChange('trainerId', e.target.value || undefined)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">No trainer assigned</option>
                        {trainers.length === 0 ? (
                          <option disabled>No trainers found</option>
                        ) : (
                          trainers.map(trainer => (
                            <option key={trainer.id} value={trainer.id}>
                              {trainer.firstName} {trainer.lastName}
                              {trainer.roles.length > 1 && ` (${trainer.roles.join(', ').toLowerCase()})`}
                            </option>
                          ))
                        )}
                      </select>
                      {trainers.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">Loading trainers...</p>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingUser.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Active User</span>
                      </label>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        onClick={handleCancelEdit}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white active:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveUser}
                        className="flex-1 py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 active:bg-blue-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
